import React, { useState, useEffect } from 'react';
import '../styles/analytics-responsive.css';
import { cleanCategoryName, formatYAxisNumber } from '../utils';

import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';

// ─── DATA ────────────────────────────────────────────────────────────────────
const defaultHealthData = {
  statusBreakdown: { pending: '0.0', approved: '0.0', rejected: '0.0', shortlisted: '0.0' }
};

// ─── STAT CARD ───────────────────────────────────────────────────────────────
const StatCard = ({ label, value, sub, change, changeColor }) => {
  const isPositive = changeColor === '#27ae60';
  return (
    <div className="analytics-stat-card" style={{
      background: '#fff',
      border: '1px solid #e8edf2',
      borderRadius: 8,
      padding: '14px 16px',
      flex: 1,
      minWidth: 0,
    }}>
      <div className="stat-label" style={{ fontSize: 14, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, whiteSpace: 'nowrap', marginBottom: 4 }}>{label}</div>
      <div className="stat-value" style={{ fontSize: 26, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.1, marginBottom: 2 }}>{value}</div>
      <div style={{ fontSize: 12, color: '#aaa', fontWeight: 500, marginBottom: 4 }}>{sub}</div>
      {change && <div style={{ fontSize: 12, color: changeColor || '#27ae60', fontWeight: 700 }}>{isPositive ? '▲' : '▼'} {change}</div>}
    </div>
  );
};

// ─── REUSABLE NO-DATA MESSAGE ────────────────────────────────────────────────
const NoDataMessage = ({ message = "No data available" }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
    color: '#aaa',
    fontSize: 14,
    fontWeight: 500,
    minHeight: 160,
  }}>
    {message}
  </div>
);

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const getTotal = (data) => data.reduce((sum, d) => sum + d.value, 0);

const RADIAN = Math.PI / 180;

const buildPieLabelLayout = (data, cx, cy, outerRadius) => {
  const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
  const labelRadius = outerRadius + 28;
  const bendOffset = 12;
  const minGap = 15;

  let cumulative = 0;
  const items = data.map((d) => {
    const startAngle = (cumulative / total) * 360;
    cumulative += d.value;
    const endAngle = (cumulative / total) * 360;
    const midAngle = (startAngle + endAngle) / 2;
    const percent = d.value / total;

    const edgeX = cx + outerRadius * Math.cos(-midAngle * RADIAN);
    const edgeY = cy + outerRadius * Math.sin(-midAngle * RADIAN);
    const rawY = cy + labelRadius * Math.sin(-midAngle * RADIAN);
    const side = Math.cos(-midAngle * RADIAN) >= 0 ? 'right' : 'left';

    return { name: d.name, color: d.color, percent, edgeX, edgeY, rawY, side };
  });

  ['left', 'right'].forEach((side) => {
    const group = items
      .map((item, i) => ({ ...item, i }))
      .filter((item) => item.side === side)
      .sort((a, b) => a.rawY - b.rawY);

    for (let k = 1; k < group.length; k++) {
      if (group[k].rawY - group[k - 1].rawY < minGap) {
        group[k].rawY = group[k - 1].rawY + minGap;
      }
    }
    group.forEach((item) => { items[item.i].finalY = item.rawY; });
  });

  items.forEach((item) => {
    if (item.finalY === undefined) item.finalY = item.rawY;
    item.finalX = item.side === 'right' ? cx + labelRadius : cx - labelRadius;
    item.bendX = item.side === 'right' ? cx + outerRadius + bendOffset : cx - outerRadius - bendOffset;
  });

  return items;
};

const Customeranalytics = () => {
  const [activeFilter, setActiveFilter] = useState('1yr');
  const [kpiData, setKpiData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [levelData, setLevelData] = useState([]);
  const [stateData, setStateData] = useState([]);
  const [healthData, setHealthData] = useState(defaultHealthData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = ['1mo', '3mo', '6mo', '1yr'];

  const renderCustomLabel = (props) => {
    const { cx, cy, outerRadius, index, percent } = props;
    if (percent < 0.005) return null;

    const layout = buildPieLabelLayout(pieChartData, cx, cy, outerRadius);
    const pos = layout[index];
    if (!pos) return null;

    const _n = cleanCategoryName(pos.name); const shortName = _n.length > 16 ? _n.slice(0, 14) + '…' : _n;
    const anchor = pos.side === 'right' ? 'start' : 'end';
    const textX = pos.finalX + (pos.side === 'right' ? 6 : -6);

    return (
      <g>
        <polyline
          points={`${pos.edgeX},${pos.edgeY} ${pos.bendX},${pos.finalY} ${pos.finalX},${pos.finalY}`}
          fill="none"
          stroke="#c7c7c7"
          strokeWidth={1}
        />
        <circle cx={pos.finalX} cy={pos.finalY} r={2} fill={pos.color || '#999'} />
        <text x={textX} y={pos.finalY} textAnchor={anchor} dominantBaseline="central" style={{ fontSize: 15 }}>
          <tspan fill="#555">{shortName}</tspan>
          <tspan fill="#1a1a2e" fontWeight={700}>{`  ${(pos.percent * 100).toFixed(0)}%`}</tspan>
        </text>
      </g>
    );
  };

  useEffect(() => {
    const fetchKpis = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const API = 'https://searchmytenders.in';
        const res = await fetch(`${API}/api/customer-analytics/kpis?filter=${activeFilter}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) {
          setKpiData(data.kpiData);
          if (data.chartData) {
            let parsedData = data.chartData;
            if (activeFilter === '1yr' || activeFilter === '6mo' || activeFilter === '3mo') {
              const monthsCount = activeFilter === '1yr' ? 12 : (activeFilter === '6mo' ? 6 : 3);
              const now = new Date();
              const padded = [];
              for (let i = monthsCount - 1; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const label = d.toLocaleString('en-US', { month: 'short' });
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Tracked: 0, Won: 0 });
              }
              parsedData = padded;
            } else if (activeFilter === '1mo') {
              const now = new Date();
              const padded = [];
              const startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
              const daysDiff = Math.floor((now - startDate) / (1000 * 60 * 60 * 24));
              for (let i = daysDiff; i >= 0; i--) {
                const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
                const label = `${d.getDate()} ${d.toLocaleString('en-US', { month: 'short' })}`;
                const existing = parsedData.find(r => r.month === label);
                padded.push(existing || { month: label, Tracked: 0, Won: 0 });
              }
              parsedData = padded;
            }
            setChartData(parsedData);
          }
          if (data.pieChartData) setPieChartData(data.pieChartData);
          if (data.levelData) setLevelData(data.levelData);
          if (data.stateData) setStateData(data.stateData);
          if (data.healthData) setHealthData(data.healthData);
        } else {
          setError(data.message || 'Failed to load data');
        }
      } catch (err) {
        console.error('Failed to fetch KPI data', err);
        setError('Network error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchKpis();
  }, [activeFilter]);

  return (
    <div className="analytics-root" style={{
      minWidth: 1024,
      fontFamily: "'Segoe UI', sans-serif",
      color: '#1a1a2e',
      padding: '0 4px'
    }}>

      <style>{`
        .recharts-wrapper svg,
        .recharts-wrapper svg * {
          outline: none !important;
        }
      `}</style>

      {/* ── PAGE TITLE + FILTER ── */}
      <div className="analytics-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <h4 style={{ fontWeight: 700, fontSize: 20, marginBottom: 2, color: '#1a1a2e' }}>Customer Analytics Dashboard</h4>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>Tender intelligence and tracking for your pipeline</p>
        </div>
        <div className="analytics-filters" style={{ display: 'flex', gap: 8 }}>
          {filters.map(f => (
            <button
              key={f}
              onClick={() => {
                if (activeFilter !== f) {
                  setLoading(true);
                  setActiveFilter(f);
                }
              }}
              style={{
                padding: '6px 18px',
                border: activeFilter === f ? '2px solid #00897b' : '1.5px solid #e0e0e0',
                borderRadius: 50,
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                background: activeFilter === f ? '#e0f5f3' : '#fff',
                color: activeFilter === f ? '#00897b' : '#888',
                transition: 'all 0.2s',
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── STAT CARDS ROW ── */}
      <div className="analytics-stats-row" style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        {error ? (
          <div style={{ padding: '20px', color: '#e74c3c', fontSize: 14, fontWeight: 'bold' }}>Error: {error}</div>
        ) : loading ? (
          <div style={{ padding: '20px', color: '#888', fontSize: 14 }}>Loading KPIs...</div>
        ) : !kpiData ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', padding: '30px 0', color: '#aaa', fontSize: 14, fontWeight: 500 }}>
            No data available
          </div>
        ) : (
          <>
            <StatCard label="Total Tenders Tracked" value={kpiData.tendersListed.value} sub="This period" change={`${Math.abs(kpiData.tendersListed.change)}% vs last period`} changeColor={kpiData.tendersListed.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Active Opportunities" value={kpiData.activeOpportunities.value} sub="Pending / Shortlisted" change={`${Math.abs(kpiData.activeOpportunities.change)}% vs last period`} changeColor={kpiData.activeOpportunities.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Awarded Tenders" value={kpiData.awardedTenders.value} sub="Approved tenders" change={`${Math.abs(kpiData.awardedTenders.change)}% vs last period`} changeColor={kpiData.awardedTenders.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Lost Tenders" value={kpiData.lostTenders.value} sub="Rejected tenders" change={`${Math.abs(kpiData.lostTenders.change)}% vs last period`} changeColor={kpiData.lostTenders.change <= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Total Pipeline Worth" value={kpiData.platformWorth.value} sub="Active opportunities" change={`${Math.abs(kpiData.platformWorth.change)}% vs last period`} changeColor={kpiData.platformWorth.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="Total Won Value" value={kpiData.wonWorth.value} sub="Awarded tenders" change={`${Math.abs(kpiData.wonWorth.change)}% vs last period`} changeColor={kpiData.wonWorth.change >= 0 ? "#27ae60" : "#e74c3c"} />
            <StatCard label="True Win Rate" value={kpiData.winRate.value} sub="Awarded vs Lost" change={`${Math.abs(kpiData.winRate.change)}% vs last period`} changeColor={kpiData.winRate.change >= 0 ? "#27ae60" : "#e74c3c"} />
          </>
        )}
      </div>

      {/* ── ROW 2: AREA CHART + PIE CHART ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        {/* Monthly Tender Activity */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 58%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
            {activeFilter === '1yr' ? 'Yearly Tender Activity' : 'Monthly Tender Activity'}
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Tenders tracked and won over time</div>
          <div style={{ flex: 1, minHeight: 200 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : chartData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorListed" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1565C0" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#1565C0" stopOpacity={0.3} />
                    </linearGradient>
                    <linearGradient id="colorOpp" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#27ae60" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#27ae60" stopOpacity={0.25} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tick={{ dy: 10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} interval={activeFilter === '1mo' ? 2 : 'preserveStartEnd'} />
                  <YAxis tickFormatter={formatYAxisNumber} tick={{ dx: -10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ fontSize: 13, fontWeight: 500, borderRadius: 6, border: '1px solid #e0e0e0' }} />
                  <Legend iconSize={18} wrapperStyle={{ fontSize: 14, paddingTop: 8, fontWeight: 500 }} formatter={(value) => <span style={{ color: '#444' }}>{value}</span>} />
                  <Area type="monotone" dataKey="Tracked" stroke="#1565C0" strokeWidth={2} fill="url(#colorListed)" dot={false} />
                  <Area type="monotone" dataKey="Won" stroke="#27ae60" strokeWidth={2} fill="url(#colorOpp)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Tenders by Status */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 38%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>Tenders by Status</div>
          </div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 4 }}>Distribution of your tracked tenders</div>
          <div style={{ flex: 1, minHeight: 230 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : pieChartData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 16, right: 65, left: 65, bottom: 16 }}>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={55}
                    dataKey="value"
                    labelLine={false}
                    label={renderCustomLabel}
                    isAnimationActive={false}
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke="#fff" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [`${value} Tenders`, name]} contentStyle={{ fontSize: 12, fontWeight: 500, borderRadius: 6 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Legend */}
          {!loading && pieChartData.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8 }}>
              {[...pieChartData]
                .sort((a, b) => b.value - a.value)
                .map(c => {
                  const total = getTotal(pieChartData);
                  const pct = total ? ((c.value / total) * 100).toFixed(0) : 0;
                  return (
                    <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 15 }}>
                      <div style={{ width: 8, height: 8, borderRadius: 2, background: c.color, flexShrink: 0 }} />
                      <span style={{ color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cleanCategoryName(c.name)}</span>
                      <span style={{ color: '#333', fontWeight: 600 }}>{pct}%</span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* ── ROW 3: HORIZONTAL BAR + STATE BAR ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 18 }}>

        {/* Level Distribution */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 45%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>Level Distribution</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Tenders categorized by priority level</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : levelData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={levelData} margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ dy: 10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <YAxis tickFormatter={formatYAxisNumber} type="category" dataKey="name" tick={{ dx: -10, fontSize: 16, fill: '#444', fontWeight: 600 }} width={80} axisLine={false} tickLine={false} />
                  <Tooltip cursor={false} formatter={(value, name) => [`${value} Tenders`, name]} contentStyle={{ fontSize: 12, fontWeight: 500, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#9b59b6" radius={[0, 3, 3, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* State-wise Distribution */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 50%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>State-wise Distribution</div>
          <div style={{ fontSize: 12, color: '#888', fontWeight: 700, textTransform: 'none', letterSpacing: 0.3, marginBottom: 12 }}>Tender count by state (top 8)</div>
          <div style={{ flex: 1, minHeight: 220 }}>
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#888', fontSize: 12 }}>Loading chart...</div>
            ) : stateData.length === 0 ? (
              <NoDataMessage />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stateData} margin={{ top: 0, right: 10, left: 0, bottom: 70 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                  <XAxis dataKey="state" tick={{ dy: 10, fontSize: 11, fill: '#444', fontWeight: 600 }} axisLine={false} tickLine={false} angle={-35} textAnchor="end" interval={0} />
                  <YAxis tickFormatter={formatYAxisNumber} allowDecimals={false} tick={{ dx: -10, fontSize: 16, fill: '#777', fontWeight: 500 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={false} formatter={(value, name) => [`${value} Tenders`, name]} contentStyle={{ fontSize: 12, fontWeight: 500, borderRadius: 6 }} />
                  <Bar dataKey="value" fill="#1565c0" radius={[3, 3, 0, 0]} barSize={22} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ── ROW 4: PIPELINE HEALTH ── */}
      <div className="analytics-chart-row" style={{ display: 'flex', gap: 14, marginBottom: 8 }}>
        {/* Pipeline Health Overview */}
        <div className="analytics-chart-panel" style={{ flex: '1 1 100%', background: '#fff', border: '1px solid #e8edf2', borderRadius: 8, padding: '16px 20px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>Pipeline Health Overview</div>

          {/* 4 metric boxes */}
          <div className="analytics-health-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Pending', value: `${healthData.statusBreakdown.pending}%`, color: '#f5a623' },
              { label: 'Shortlisted', value: `${healthData.statusBreakdown.shortlisted}%`, color: '#2ecc71' },
              { label: 'Approved', value: `${healthData.statusBreakdown.approved}%`, color: '#27ae60' },
              { label: 'Rejected', value: `${healthData.statusBreakdown.rejected}%`, color: '#e74c3c' },
            ].map(m => (
              <div key={m.label} style={{ border: '1px solid #e8edf2', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 4, fontWeight: 600 }}>{m.label}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: m.color }}>{m.value}</div>
              </div>
            ))}
          </div>

          {/* Status breakdown bar */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 15, color: '#aaa', fontWeight: 500, marginBottom: 6 }}>Pipeline distribution</div>
            <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
              <div style={{ flex: healthData.statusBreakdown.pending, background: '#f5a623' }} />
              <div style={{ flex: healthData.statusBreakdown.shortlisted, background: '#2ecc71' }} />
              <div style={{ flex: healthData.statusBreakdown.approved, background: '#27ae60' }} />
              <div style={{ flex: healthData.statusBreakdown.rejected, background: '#e74c3c' }} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Customeranalytics;